import apiClient from '@/shared/api/client';
import { ApiResponse, PaginatedResponse } from '@/shared/api/types';

// ─── Legacy types ──────────────────────────────────────────────────────────────

export interface SEOMetrics {
  visibilityScore: number;
  visibilityChange: number;
  organicTraffic: number;
  trafficChange: number;
  backlinksCount: number;
  backlinksChange: number;
  domainAuthority: number;
  aeoScore: number;
}

// ─── Audit types ───────────────────────────────────────────────────────────────

export interface AuditCheck {
  id: string;
  name: string;
  status: 'pass' | 'fail' | 'unable_to_verify';
  current_value?: string | null;
  expected_value?: string | null;
  recommendation?: string | null;
  priority?: 'p0' | 'p1' | 'p2' | 'p3' | null;
  note?: string | null;
}

export interface SectionScore {
  score: number;
  weight: number;
  checks: AuditCheck[];
}

export interface ActionPlanItem {
  check_id: string;
  task: string;
  impact: string;
  effort: 'low' | 'medium' | 'high';
}

export interface AuditResult {
  id: string;
  locationId: string;
  auditType: 'initial' | 'weekly' | 'manual';
  overallScore: number | null;
  grade: string | null;
  trustScore: number | null;
  sectionScores: {
    technical_seo: SectionScore;
    on_page_seo: SectionScore;
    local_seo: SectionScore;
    off_page_seo: SectionScore;
    schema_structured_data: SectionScore;
    aeo_ai_visibility: SectionScore;
    conversion_ux: SectionScore;
  } | null;
  checks: AuditCheck[] | null;
  actionPlan: {
    p0: ActionPlanItem[];
    p1: ActionPlanItem[];
    p2: ActionPlanItem[];
    p3: ActionPlanItem[];
  } | null;
  aiReadiness: {
    overall: 'high' | 'medium' | 'low';
    factors_positive: string[];
    factors_negative: string[];
    engines: Array<{ name: string; likelihood: 'high' | 'medium' | 'low'; reasoning: string }>;
  } | null;
  executiveSummary: {
    overall_score: number;
    grade: string;
    trust_score: number;
    top_3_wins: string[];
    top_3_urgent_fixes: string[];
    owner_friendly_summary: string;
    insight_cards?: Array<{
      type: 'green' | 'yellow' | 'red';
      icon: string;
      text: string;
      context: string | null;
    }>;
  } | null;
  keywordRecs: KeywordIntelligence[] | null;
  createdAt: string;
}

export interface AuditOverview {
  latestAudit: AuditResult | null;
  isRunning: boolean;
  runningStatus: 'pending' | 'in_progress' | null;
  sitemapUrlCount: number | null;
}

/** Metadata attached to individual audit progress steps. */
export interface AuditStepMeta {
  keywordCount?: number;
  reviewCount?: number;
  found?: boolean;
  urlCount?: number;
  pagesCrawled?: number;
  cached?: boolean;
  valid?: boolean;
  daysUntilExpiry?: number;
  mobileScore?: number;
  total?: number;
  overallScore?: number;
  grade?: string;
  /** Why a step was skipped instead of run — e.g. gsc_sync/gbp_sync when the
   * integration isn't connected, or connected but already fresh. */
  reason?: 'not_connected' | 'cached';
  /** Which crawl transport ultimately succeeded — set on the 'crawl_pages' step. */
  transport?: 'http' | 'playwright' | 'firecrawl';
}

export interface AuditProgressStep {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'done' | 'failed' | 'skipped';
  error?: string;
  meta?: AuditStepMeta;
}

export interface AuditStatus {
  id: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  pagesFetched: number;
  createdAt: string;
  updatedAt: string;
  errorMessage: string | null;
  progress: AuditProgressStep[] | null;
}

// ─── Keyword types ─────────────────────────────────────────────────────────────

export interface KeywordIntelligence {
  id: string;
  locationId: string;
  source: 'gsc' | 'ai_inferred';
  query: string;
  impressions: number | null;
  clicks: number | null;
  ctr: number | null;
  avgPosition: number | null;
  intent: 'navigational' | 'informational' | 'commercial' | 'transactional' | null;
  isBranded: boolean;
  volumeEstimate: 'high' | 'medium' | 'low' | null;
  currentPageUrl: string | null;
  opportunityScore: number | null;
  recommendation: string | null;
  createdAt: string;
}

// ─── Content types ─────────────────────────────────────────────────────────────

export interface ContentOpportunity {
  id: string;
  topic: string;
  targetKeyword: string;
  contentType: string;
  volumeEstimate: string | null;
  status: string;
  generatedDraft: string | null;
  createdAt: string;
}

// ─── Site health types ─────────────────────────────────────────────────────────

export interface PageSpeedResult {
  url: string;
  strategy: string;
  metrics: { lcp?: number; fcp?: number; inp?: number; cls?: number; ttfb?: number };
}

export interface SiteAnalysis {
  pagespeedResults?: PageSpeedResult[];
}

export interface PageSpeedMetric {
  name: string;
  value: string;
  target: string;
  status: 'fast' | 'average' | 'slow';
  pct: number;
}

// ─── Visibility types ─────────────────────────────────────────────────────────

export interface VisibilityOverview {
  reviewCount: number | null;
  reviewRating: number | null;
  /** `gbp` = synced from a connected profile; `public_scan` = verified public listing. */
  reviewSource: 'gbp' | 'public_scan' | null;
  /** Listing name a `public_scan` figure was matched to, for attribution. */
  reviewSourceName: string | null;
  integrations: Record<string, string>;
}

export interface DailyClick {
  id: string;
  locationId: string;
  date: string;
  clicks: number;
  impressions: number;
}

export interface SeoContentItem {
  id: string;
  locationId: string;
  title: string;
  type: string;
  bucket: string;
  campaignId: string | null;
  status: string;
  blocker: string | null;
  draftSummary: string | null;
  detail: string | null;
  targetKeywords: string[] | null;
  wordCount: string | null;
  needsAccess: string[] | null;
  changeRequest: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  // Plan generation fields
  seoContentPlanId: string | null;
  strategyItemIndex: number | null;
  strategyItemTitle: string | null;
  week: number | null;
  language: string | null;
  brief: string | null;
  formatNotes: string | null;
  targetAudience: string | null;
  channel: string | null;
  priority: string | null;
  sections: string[] | null;
  cta: string | null;
  auditResultId: string | null;
}

// ─── API client ────────────────────────────────────────────────────────────────

export const seoApi = {
  // Legacy
  getMetrics: async (locationId: string): Promise<ApiResponse<SEOMetrics>> => {
    const response = await apiClient.get<ApiResponse<SEOMetrics>>('/seo/metrics', {
      params: { locationId },
    });
    return response.data;
  },

  // Audit
  getOverview: async (locationId: string): Promise<ApiResponse<AuditOverview>> => {
    const response = await apiClient.get<ApiResponse<AuditOverview>>('/seo/overview', {
      params: { locationId },
    });
    return response.data;
  },

  triggerAudit: async (
    locationId: string,
    auditType?: 'initial' | 'weekly' | 'manual',
    isolated?: boolean,
    skipCache?: boolean,
    recrawl?: boolean,
  ): Promise<ApiResponse<{ queued: boolean; message: string }>> => {
    const response = await apiClient.post<ApiResponse<{ queued: boolean; message: string }>>(
      '/seo/audit',
      { locationId, auditType, isolated, skipCache, recrawl },
    );
    return response.data;
  },

  cancelAudit: async (
    locationId: string,
  ): Promise<ApiResponse<{ cancelled: boolean; message: string }>> => {
    const response = await apiClient.post<ApiResponse<{ cancelled: boolean; message: string }>>(
      '/seo/audit/cancel',
      { locationId },
    );
    return response.data;
  },

  getAuditStatus: async (locationId: string): Promise<ApiResponse<AuditStatus | null>> => {
    const response = await apiClient.get<ApiResponse<AuditStatus | null>>('/seo/audit/status', {
      params: { locationId },
    });
    return response.data;
  },

  getLatestAudit: async (locationId: string): Promise<ApiResponse<AuditResult | null>> => {
    const response = await apiClient.get<ApiResponse<AuditResult | null>>('/seo/audit/latest', {
      params: { locationId },
    });
    return response.data;
  },

  // Keywords
  getKeywords: async (
    locationId: string,
    params?: { page?: number; limit?: number; source?: string; intent?: string; branded?: string },
  ): Promise<
    PaginatedResponse<KeywordIntelligence> & { activeSource: 'gsc' | 'ai_inferred' | 'merged' }
  > => {
    const response = await apiClient.get<
      PaginatedResponse<KeywordIntelligence> & { activeSource: 'gsc' | 'ai_inferred' | 'merged' }
    >('/seo/keywords', {
      params: { locationId, ...params },
    });
    return response.data;
  },

  // Content
  getContent: async (locationId: string): Promise<ApiResponse<ContentOpportunity[]>> => {
    const response = await apiClient.get<ApiResponse<ContentOpportunity[]>>('/seo/content', {
      params: { locationId },
    });
    return response.data;
  },

  // Site health
  getSiteHealth: async (
    locationId: string,
  ): Promise<ApiResponse<{ audit: AuditResult | null; analysis: SiteAnalysis | null }>> => {
    const response = await apiClient.get<
      ApiResponse<{ audit: AuditResult | null; analysis: SiteAnalysis | null }>
    >('/seo/site-health', { params: { locationId } });
    return response.data;
  },

  // Visibility overview (reviews, integrations)
  getVisibilityOverview: async (locationId: string): Promise<ApiResponse<VisibilityOverview>> => {
    const response = await apiClient.get<ApiResponse<VisibilityOverview>>(
      '/seo/visibility-overview',
      { params: { locationId } },
    );
    return response.data;
  },

  // Performance (daily clicks)
  getDailyClicks: async (locationId: string): Promise<ApiResponse<DailyClick[]>> => {
    const response = await apiClient.get<ApiResponse<DailyClick[]>>(
      '/seo/performance/daily-clicks',
      { params: { locationId } },
    );
    return response.data;
  },

  // Content items
  getContentItems: async (
    locationId: string,
    filters?: { bucket?: string; status?: string },
  ): Promise<ApiResponse<SeoContentItem[]>> => {
    const response = await apiClient.get<ApiResponse<SeoContentItem[]>>('/seo/content-items', {
      params: { locationId, ...filters },
    });
    return response.data;
  },

  updateContentItem: async (id: string, status: string): Promise<ApiResponse<SeoContentItem>> => {
    const response = await apiClient.patch<ApiResponse<SeoContentItem>>(
      `/seo/content-items/${id}`,
      { status },
    );
    return response.data;
  },

  submitContentFeedback: async (
    id: string,
    feedback: string,
  ): Promise<ApiResponse<SeoContentItem>> => {
    const response = await apiClient.post<ApiResponse<SeoContentItem>>(
      `/seo/content-items/${id}/feedback`,
      { feedback },
    );
    return response.data;
  },

  generateContentPlan: async (locationId: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await apiClient.post<ApiResponse<{ message: string }>>(
      '/seo/content-items/generate',
      { locationId },
    );
    return response.data;
  },

  getContentPreference: async (
    locationId: string,
  ): Promise<ApiResponse<{ preference: string }>> => {
    const response = await apiClient.get<ApiResponse<{ preference: string }>>(
      '/seo/content-preference',
      { params: { locationId } },
    );
    return response.data;
  },

  updateContentPreference: async (
    locationId: string,
    preference: 'auto' | 'review',
  ): Promise<ApiResponse<{ preference: string }>> => {
    const response = await apiClient.patch<ApiResponse<{ preference: string }>>(
      '/seo/content-preference',
      { locationId, preference },
    );
    return response.data;
  },
};
